// reset-password.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { authBackendEndPointToken } from '../app.config';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <div class="container-fluid">
      <!-- Top Navigation -->
      <div class="top-nav">
        <a routerLink="/register" routerLinkActive="router-link-active">Register</a>
        <a routerLink="/login" routerLinkActive="router-link-active">Sign In</a>
      </div>

      <!-- Title -->
      <div class="maths-title">
        <h1>Elikita2</h1>
      </div>

      <!-- Main Card -->
      <div class="main-wrapper">
        <div class="register-wrapper">
          <!-- Token Expired/Invalid State -->
          <div *ngIf="tokenExpired || tokenInvalid" class="error-state">
            <mat-icon class="error-icon">error_outline</mat-icon>
            <div class="form-title">
              <div class="form-name">{{ tokenExpired ? 'Link Expired' : 'Invalid Link' }}</div>
              <div class="form-description">
                {{ tokenExpired
                  ? 'Your password reset link has expired. Please request a new one.'
                  : 'This password reset link is invalid or has already been used.' }}
              </div>
            </div>
            <button
              mat-flat-button
              color="primary"
              class="primary-button"
              routerLink="/login">
              Back to Sign In
            </button>
          </div>

          <!-- Success State -->
          <div *ngIf="resetSuccess" class="success-state">
            <mat-icon class="success-icon">check_circle</mat-icon>
            <div class="form-title">
              <div class="form-name">Password Reset Successful!</div>
              <div class="form-description">
                Your password has been successfully reset. You can now sign in with your new password.
              </div>
            </div>
            <button
              mat-flat-button
              color="primary"
              class="primary-button"
              routerLink="/login">
              Continue to Sign In
            </button>
          </div>

          <!-- Reset Password Form -->
          <div *ngIf="!tokenExpired && !tokenInvalid && !resetSuccess">
            <div class="form-title">
              <div class="form-name">Reset Your Password</div>
              <div class="form-description">Enter your new password below</div>
            </div>

            <form [formGroup]="resetPasswordForm" (ngSubmit)="onSubmit()">
              <!-- Email Display (Read-only) -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Email</mat-label>
                <mat-icon matPrefix>mail</mat-icon>
                <input matInput type="email" [value]="email" readonly />
              </mat-form-field>

              <!-- New Password -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>New Password</mat-label>
                <mat-icon matPrefix>lock</mat-icon>
                <input
                  matInput
                  [type]="showNewPassword ? 'text' : 'password'"
                  formControlName="newPassword"
                  required />
                <button
                  mat-icon-button
                  matSuffix
                  (click)="showNewPassword = !showNewPassword"
                  type="button">
                  <mat-icon>{{ showNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <mat-error *ngIf="resetPasswordForm.get('newPassword')?.hasError('required')">
                  Password is required
                </mat-error>
                <mat-error *ngIf="resetPasswordForm.get('newPassword')?.hasError('minlength')">
                  Password must be at least 6 characters
                </mat-error>
              </mat-form-field>

              <!-- Confirm Password -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Confirm New Password</mat-label>
                <mat-icon matPrefix>lock</mat-icon>
                <input
                  matInput
                  [type]="showConfirmPassword ? 'text' : 'password'"
                  formControlName="confirmPassword"
                  required />
                <button
                  mat-icon-button
                  matSuffix
                  (click)="showConfirmPassword = !showConfirmPassword"
                  type="button">
                  <mat-icon>{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <mat-error *ngIf="resetPasswordForm.get('confirmPassword')?.hasError('required')">
                  Please confirm your password
                </mat-error>
                <mat-error *ngIf="resetPasswordForm.get('confirmPassword')?.hasError('passwordMismatch')">
                  Passwords do not match
                </mat-error>
              </mat-form-field>

              <!-- Password Requirements -->
              <div class="password-requirements">
                <div class="requirement-title">Password must contain:</div>
                <div class="requirement-item" [class.met]="hasMinLength">
                  <mat-icon>{{ hasMinLength ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                  <span>At least 6 characters</span>
                </div>
              </div>

              <!-- Error Message -->
              <div class="error-message" *ngIf="errorMessage">
                <mat-icon>error</mat-icon>
                <span>{{ errorMessage }}</span>
              </div>

              <!-- Submit Button -->
              <button
                mat-flat-button
                color="primary"
                class="primary-button"
                type="submit"
                [disabled]="loading || resetPasswordForm.invalid">
                {{ loading ? 'Resetting Password...' : 'Reset Password' }}
              </button>

              <!-- Back to Sign In -->
              <div class="back-to-login">
                <a routerLink="/login">Back to Sign In</a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .container-fluid {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      padding: 20px;
    }

    .top-nav {
      display: flex;
      justify-content: flex-end;
      gap: 20px;
      margin-bottom: 20px;

      a {
        color: blue;
        text-decoration: none;
        font-weight: 500;
        padding: 8px 16px;
        border-radius: 4px;
        transition: background-color 0.3s;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        &.router-link-active {
          background-color: rgba(255, 255, 255, 0.2);
        }
      }
    }

    .maths-title {
      text-align: center;
      margin-bottom: 30px;

      h1 {
        color: green;
        font-size: 48px;
        font-weight: 700;
        margin: 0;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
      }
    }

    .main-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
    }

    .register-wrapper {
      background: white;
      border-radius: 16px;
      padding: 40px;
      width: 100%;
      max-width: 480px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    }

    .form-title {
      text-align: center;
      margin-bottom: 30px;

      .form-name {
        font-size: 28px;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 8px;
      }

      .form-description {
        font-size: 14px;
        color: #666;
        line-height: 1.5;
      }
    }

    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }

    .password-requirements {
      background: #f5f5f5;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;

      .requirement-title {
        font-size: 13px;
        font-weight: 600;
        color: #333;
        margin-bottom: 12px;
      }

      .requirement-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #666;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
          color: #ccc;
        }

        &.met {
          color: #4caf50;

          mat-icon {
            color: #4caf50;
          }
        }
      }
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #d32f2f;
      font-size: 13px;
      margin-bottom: 16px;
      padding: 12px;
      background-color: #ffebee;
      border-radius: 8px;

      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
      }
    }

    .primary-button {
      width: 100%;
      height: 48px;
      font-size: 16px;
      font-weight: 600;
      text-transform: none;
      border-radius: 8px;
      margin-bottom: 16px;
    }

    .back-to-login {
      text-align: center;
      margin-top: 16px;

      a {
        color: #667eea;
        text-decoration: none;
        font-size: 14px;
        font-weight: 500;
        transition: color 0.3s;

        &:hover {
          color: #764ba2;
          text-decoration: underline;
        }
      }
    }

    // Success State
    .success-state {
      text-align: center;

      .success-icon {
        font-size: 80px;
        width: 80px;
        height: 80px;
        color: #4caf50;
        margin-bottom: 20px;
      }

      .form-title {
        margin-bottom: 30px;
      }
    }

    // Error State
    .error-state {
      text-align: center;

      .error-icon {
        font-size: 80px;
        width: 80px;
        height: 80px;
        color: #f44336;
        margin-bottom: 20px;
      }

      .form-title {
        margin-bottom: 30px;
      }
    }

    ::ng-deep {
      .mat-mdc-form-field {
        .mat-mdc-text-field-wrapper {
          background-color: #fafafa;
        }
      }

      .mat-mdc-outlined .mat-mdc-notch {
        border-color: #e0e0e0 !important;
      }
    }

    @media (max-width: 600px) {
      .register-wrapper {
        padding: 30px 20px;
      }

      .maths-title h1 {
        font-size: 36px;
      }
    }
  `]
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm: FormGroup;
  email = '';
  token = '';
  loading = false;
  resetSuccess = false;
  tokenExpired = false;
  tokenInvalid = false;
  errorMessage = '';
  showNewPassword = false;
  showConfirmPassword = false;

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
authBackendApiBaseUrl = inject(authBackendEndPointToken)
  constructor() {
    this.resetPasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Get email and token from URL query parameters
    this.route.queryParams.subscribe(params => {
      this.email = params['email'] || '';
      this.token = params['token'] || '';

      if (!this.email || !this.token) {
        this.tokenInvalid = true;
      }
    });
  }

  // Custom validator to check if passwords match
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    if (confirmPassword.value === '') {
      return null;
    }

    if (newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      const errors = confirmPassword.errors;
      if (errors) {
        delete errors['passwordMismatch'];
        if (Object.keys(errors).length === 0) {
          confirmPassword.setErrors(null);
        }
      }
      return null;
    }
  }

  get hasMinLength(): boolean {
    const password = this.resetPasswordForm.get('newPassword')?.value || '';
    return password.length >= 6;
  }

  onSubmit(): void {
    if (this.resetPasswordForm.invalid || this.loading) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const requestBody = {
      email: this.email,
      token: this.token,
      newPassword: this.resetPasswordForm.value.newPassword
    };

    // Update this URL to match your API endpoint


    this.http.post(`${this.authBackendApiBaseUrl}/api/auth/reset-password`, requestBody)
      .subscribe({
        next: (response: any) => {
          console.log('✅ Password reset successful');
          this.loading = false;
          this.resetSuccess = true;
        },
        error: (error: any) => {
          console.error('❌ Reset password error:', error);
          this.loading = false;

          if (error.status === 400) {
            const message = error.error?.message || '';
            if (message.includes('expired')) {
              this.tokenExpired = true;
            } else if (message.includes('Invalid reset token')) {
              this.tokenInvalid = true;
            } else {
              this.errorMessage = message || 'Failed to reset password. Please try again.';
            }
          } else {
            this.errorMessage = 'Failed to reset password. Please try again.';
          }
        }
      });
  }
}
